import React from 'react';

import TextArea from '@hero/ui-kit/inputs/TextArea';
import Section from '@hero/ui-kit/layout/Section';
import Form, { useFormContext } from '../../../../../components/Form';
import Loading from '../../../../../components/States/Loading';
import StickyNote, { StickyNoteActions } from '../../../../../components/StickyNote';
import { useUserStatusContext } from '../../../../../context/UserStatus';
import { MemberInfo } from '../../../types';
import { useOrganizationMemberDetailsContext } from '../../context/OrganizationMemberDetailsContext';
import {
    CONTACT_AND_CAREGIVER_FIELDS,
    ContactAndCaregiverFields
} from '../../Demographics/Forms/SupplementalForm';
import {
    SupplementalFormValues,
    getSupplementalSummary,
    supplementalSchema,
    toPartialSupplementalPayload,
    toSupplementalFormValues
} from '../../Demographics/utils/supplemental';
import useGetCustomDemographics from '../../../api/useGetCustomDemographics';
import useUpdateCustomDemographics from '../../../api/useUpdateCustomDemographics';
import Field from '../../EhrRecord/components/Field';
import { CustomDemographics } from '../../../types';
import * as Style from './style.module.scss';

// What the card edits, and so all it saves; the Demographics tab owns the rest.
const CARD_FIELDS = ['notes', ...CONTACT_AND_CAREGIVER_FIELDS] as const;

const isInactiveMember = (memberInfo?: MemberInfo) =>
    !!memberInfo?.status_info?.discharged_at || memberInfo?.status_info?.status === 'CANCELLED';

interface EditorActionsProps {
    isSaving: boolean;
    onCancel: () => void;
    errorMessage?: string;
}

// Inside the form so Save can follow its dirty state.
const EditorActions: React.FC<EditorActionsProps> = ({ isSaving, onCancel, errorMessage }) => {
    const {
        formState: { isDirty }
    } = useFormContext();

    return (
        <StickyNoteActions
            onCancel={onCancel}
            isSaving={isSaving}
            cancelDisabled={isSaving}
            saveDisabled={!isDirty || isSaving}
            errorMessage={errorMessage}
        />
    );
};

interface NotesEditorProps {
    memberId?: number | null;
    demographics?: CustomDemographics | null;
    onClose: () => void;
}

const NotesEditor: React.FC<NotesEditorProps> = ({ memberId, demographics, onClose }) => {
    const savedValues = toSupplementalFormValues(demographics);
    const update = useUpdateCustomDemographics(memberId, onClose);

    return (
        <Form
            compact
            validationMode="onTouched"
            validationSchema={supplementalSchema}
            defaultValues={savedValues}
            submitFn={(values: SupplementalFormValues) =>
                update.mutate(toPartialSupplementalPayload(values, CARD_FIELDS))
            }
        >
            <TextArea name="notes" displayName="Sticky note" rows={3} autoFocus />
            <div className={Style.fields}>
                <ContactAndCaregiverFields savedValues={savedValues} disabled={update.isPending} />
            </div>
            <EditorActions
                isSaving={update.isPending}
                onCancel={onClose}
                errorMessage={update.error?.errors?.[0]}
            />
        </Form>
    );
};

/** Sticky note and supplemental details, edited in place in the card. */
const MemberNotes: React.FC = () => {
    const { memberDetails } = useOrganizationMemberDetailsContext();
    const { permissions } = useUserStatusContext();
    const memberInfo = memberDetails?.member_info;
    const [isEditing, setIsEditing] = React.useState(false);

    const canEdit = !isInactiveMember(memberInfo) && permissions.fleet.fleetMemberEdit;

    const { data: demographics, isLoading } = useGetCustomDemographics(memberInfo?.member_id);
    const notes = demographics?.notes ?? '';
    const summary = getSupplementalSummary(demographics);

    if (isLoading) {
        return (
            <Section border>
                <Loading height={8} />
            </Section>
        );
    }

    if (!isEditing && !notes && !canEdit && summary.length === 0) return null;

    return (
        <StickyNote
            name="Sticky note"
            title="Sticky note"
            titleVariant="label"
            body={notes || null}
            canEdit={canEdit}
            addLabel="+ Add sticky note"
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            editor={
                <NotesEditor
                    memberId={memberInfo?.member_id}
                    demographics={demographics}
                    onClose={() => setIsEditing(false)}
                />
            }
        >
            {summary.length > 0 ? (
                <div className={Style.summary}>
                    {summary.map(({ label, value }) => (
                        <Field key={label} label={label} value={value} />
                    ))}
                </div>
            ) : null}
        </StickyNote>
    );
};

export default MemberNotes;
